import { BaseStore } from "@infrastructure/stores/_base.store";
import { when, runInAction } from "mobx";
import { initTelegram } from "@shared/integrations/telegram/init";
import { analytics, identifyFunnel, trackFunnel } from "@shared/utils/analytics";

export interface User {
  id: number;
  name: string | null;
  photo: string | null;
  language: string | null;
  phone_number: string | null;
}

export namespace Auth {
  export interface Authenticated {
    state: "authenticated";
    user: User;
  }

  export interface Anonymous {
    state: "anonymous";
    reauthRequired: boolean;
  }

  export interface Reauth {
    state: "reauth";
    previousUser?: User; // Сохраняем предыдущего пользователя для восстановления
  }

  export type State = Authenticated | Anonymous | Reauth;
}

type AuthStoreState = {
  auth: Auth.State;
  initDataRaw: string | null;
};

class AuthStoreFactory extends BaseStore<AuthStoreState> {
  initDataRaw!: string;
  auth: Auth.State = {
    state: "anonymous",
    reauthRequired: false
  };

  constructor() {
    super();

    const initDataRaw = initTelegram();

    if (!initDataRaw) {
      runInAction(() => {
        this.auth = { state: "anonymous", reauthRequired: false };
      });
      return;
    }

    this.initDataRaw = initDataRaw;
  }

  protected createState(): AuthStoreState {
    return {
      auth: this.auth,
      initDataRaw: this.initDataRaw || null,
    };
  }

  protected getStateKey(): string {
    return `auth:${this.auth.state}:${this.auth.state === "authenticated" ? this.auth.user.name : ""}`;
  }

  get user() {
    if (this.auth.state === "authenticated") {
      return this.auth.user;
    }
    return null;
  }

  /**
   * Устанавливает состояние авторизации
   */
  setAuth(auth: Auth.State) {
    const previousState = this.auth.state;
    
    if (auth.state === "authenticated") {
      // Идентифицируем пользователя в PostHog
      analytics.identify(auth.user.id.toString(), {
        name: auth.user.name,
        phone: auth.user.phone_number,
        language: auth.user.language
      });
      console.log(`[AuthStore] Auth state changed: ${previousState} -> authenticated (user: ${auth.user.name || auth.user.id})`);

      // Склеиваем анонимную часть пути с пользователем и отмечаем шаг воронки.
      // Только при первом переходе в authenticated: переавторизация — не новый вход.
      if (previousState !== "authenticated") {
        identifyFunnel(auth.user.id);
        trackFunnel("signup_completed", auth.user.id);

        // Код приглашения, сохранённый при заходе по реферальной ссылке,
        // применяется один раз после входа.
        import("@domain/services/referral.service").then(({ ReferralService }) => {
          void ReferralService.applyStoredCode();
        }).catch((err) => {
          console.error("[AuthStore] Failed to apply referral code:", err);
        });

        // Подарок, обещанный раздаточным материалом. Забирается сразу после
        // входа: человек пришёл именно за ним, и ждать заказа незачем. Кому
        // подарок не положен, решает сервер.
        import("@domain/services/wallet.service").then(({ WalletService }) => {
          void WalletService.claimWelcome();
        }).catch((err) => {
          console.error("[AuthStore] Failed to claim welcome gift:", err);
        });
      }

      // Инициализируем глобальное WebSocket соединение для заказов
      import("@domain/services/order.service").then(({ OrderService }) => {
        OrderService.initGlobalWebSocket(auth.user.id);
      }).catch((err) => {
        console.error("[AuthStore] Failed to init WebSocket:", err);
      });
    } else if (auth.state === "anonymous") {
      console.log(`[AuthStore] Auth state changed: ${previousState} -> anonymous (reauthRequired: ${auth.reauthRequired})`);
      
      // Закрываем WebSocket соединение при выходе
      import("@domain/services/order.service").then(({ OrderService }) => {
        OrderService.closeGlobalWebSocket();
      }).catch((err) => {
        console.error("[AuthStore] Failed to close WebSocket:", err);
      });
    } else if (auth.state === "reauth") {
      console.log(`[AuthStore] Auth state changed: ${previousState} -> reauth`);
    }
    
    this.auth = auth;
    this.notifyListeners();
  }

  /**
   * Обновляет данные пользователя
   */
  setUser(user: User) {
    if (this.auth.state === "authenticated") {
      this.auth = {
        ...this.auth,
        user,
      };
      
      // Обновляем свойства пользователя в PostHog
      analytics.setUserProperties({
        name: user.name,
        phone: user.phone_number,
        language: user.language
      });
      
      this.notifyListeners();
    }
  }

  /**
   * Ожидает авторизацию (теперь просто возвращается сразу, так как loading состояния нет)
   */
  async waitForAuth() {
    return;
  }
}

export const AuthStore = new AuthStoreFactory();
